import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Link,
  Box,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SearchIcon from "@mui/icons-material/Search";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useAuth } from "../../context/AuthContext";

// Import components
import LoginForm from "./components/LoginForm";
import PasswordReset from "./components/PasswordReset";
import TwoFactorAuth from "./components/TwoFactorAuth";
const headerLogo = "/assets/images/teamscout-logo.png";

// Styled components
const AuthContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  margin: 0,
  padding: 0,
}));

const AuthPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 0,
  boxShadow: "none",
  overflow: "visible",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  textAlign: "left",
}));

const VideoSection = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(6, 2),
  textAlign: "center",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(4, 2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3, 1),
  },
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  maxWidth: 900,
  margin: "0 auto",
  cursor: "pointer",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: theme.shadows[5],
  "&:hover .play-button": {
    transform: "translate(-50%, -50%) scale(1.1)",
  },
}));

const PlayButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  zIndex: 1,
  padding: theme.spacing(2),
  transition: "transform 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const FeaturesSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 2),
  backgroundColor: "#fff",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(6, 2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4, 1),
  },
}));

const Footer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 2),
  backgroundColor: theme.palette.grey[100],
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: "auto",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3, 1),
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: theme.shadows[8],
  },
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: "50%",
  padding: theme.spacing(1.5),
  display: "inline-flex",
  marginBottom: theme.spacing(2),
}));

const EnhancedLogin = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    login,
    error,
    clearError,
    isAuthenticated,
    requestPasswordReset,
    resetPassword,
  } = useAuth();

  // State
  const [mode, setMode] = useState("login"); // 'login', 'reset', '2fa'
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  // Handle login
  const handleLogin = async (email, password, remember) => {
    setLoading(true);
    setRememberMe(remember);

    try {
      await login(email, password);

      // Check if 2FA is required (this would be determined by your auth system)
      const requires2FA = false; // This would be determined by the login response

      if (requires2FA) {
        setMode("2fa");
      }

      // Redirect will happen automatically due to the useEffect above if login is successful
    } catch (err) {
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset request
  const handleRequestReset = async (email) => {
    setLoading(true);

    try {
      await requestPasswordReset(email);
      return Promise.resolve();
    } catch (err) {
      console.error("Password reset request error:", err);
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async (email, code, newPassword) => {
    setLoading(true);

    try {
      await resetPassword(email, code, newPassword);
      return Promise.resolve();
    } catch (err) {
      console.error("Password reset error:", err);
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle 2FA verification
  const handle2FAVerify = async (code, method) => {
    setLoading(true);

    try {
      // This would be your 2FA verification logic
      console.log(`Verifying 2FA code: ${code} via ${method}`);

      // Simulate verification
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // If successful, continue login process
      // Redirect will happen automatically due to the useEffect above
    } catch (err) {
      console.error("2FA verification error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle resend 2FA code
  const handleResend2FACode = async (method) => {
    try {
      // This would be your resend 2FA code logic
      console.log(`Resending 2FA code via ${method}`);

      // Simulate resending
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (err) {
      console.error("Resend 2FA code error:", err);
    }
  };

  // Render content based on mode
  const renderContent = () => {
    switch (mode) {
      case "reset":
        return (
          <>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
              <IconButton onClick={() => setMode("login")} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">Reset Password</Typography>
            </Box>
            <PasswordReset
              onRequestReset={handleRequestReset}
              onResetPassword={handleResetPassword}
              loading={loading}
              error={error}
              clearError={clearError}
            />
          </>
        );
      case "2fa":
        return (
          <>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
              <IconButton onClick={() => setMode("login")} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">Two-Factor Authentication</Typography>
            </Box>
            <TwoFactorAuth
              onVerify={handle2FAVerify}
              onResendCode={handleResend2FACode}
              loading={loading}
              error={error}
              clearError={clearError}
              userEmail="user@example.com" // This would come from the login form
            />
          </>
        );
      default: // 'login'
        return (
          <>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Sign in to TeamScout
            </Typography>
            <LoginForm
              onSubmit={handleLogin}
              loading={loading}
              error={error}
              clearError={clearError}
            />

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="body2">
                Don't have an account?{" "}
                <Link
                  component={RouterLink}
                  to="/register"
                  variant="body2"
                  fontWeight="bold"
                >
                  Sign Up
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setMode("reset")}
                >
                  Forgot password?
                </Link>
              </Typography>
            </Box>
          </>
        );
    }
  };

  // Features data
  const features = [
    {
      title: "Smart Data Import",
      description:
        "Take your excel spread sheets and import them directly into our platform making your data easy to see and update.",
      icon: <FileUploadIcon fontSize="large" />,
    },
    {
      title: "Recruiting Boards",
      description:
        "Quickly create recruiting boards based on class, position, evaluations, and more. Rank players by color, add evals and notes instantly.",
      icon: <DashboardIcon fontSize="large" />,
    },
    {
      title: "Advanced Search Function",
      description:
        "Set filters like height, weight, or 40 time to see specific athletes you want on your team.",
      icon: <SearchIcon fontSize="large" />,
    },
    {
      title: "Depth Chart",
      description:
        "Visualize your team's future with easy-to-create depth charts based on your current roster.",
      icon: <AccountTreeIcon fontSize="large" />,
    },
    {
      title: "Coach Database",
      description:
        "Connect with coaches nationwide! Our comprehensive database lets you find high school coaches by state, region, name, or school.",
      icon: <DashboardIcon fontSize="large" />,
    },
    {
      title: "Time Saver",
      description:
        "Get back hours of time every week when you use our simple dashboard your entire team will love.",
      icon: <DashboardIcon fontSize="large" />,
    },
  ];

  return (
    <AuthContainer>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "white",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 1, sm: 2, md: 3 },
            maxWidth: "lg",
            mx: "auto",
            width: "100%",
            alignItems: "center",
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          <LogoContainer sx={{ flexGrow: 1 }}>
            <img
              src={headerLogo}
              alt="TeamScout"
              style={{
                height: isMobile ? 40 : 50,
                maxWidth: "100%",
                display: "block",
              }}
            />
          </LogoContainer>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setLoginDialogOpen(true)}
            size={isMobile ? "small" : "medium"}
            sx={{
              minWidth: { xs: "auto", sm: "64px" },
              px: { xs: 1.5, sm: 2 },
              alignSelf: "center",
            }}
          >
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      <VideoSection>
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, mx: "auto" }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            gutterBottom
            align="center"
            sx={{ px: { xs: 1, sm: 0 } }}
          >
            Streamline Your Recruiting Process
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "subtitle1"}
            color="text.secondary"
            align="center"
            sx={{
              mb: 4,
              maxWidth: 700,
              mx: "auto",
              px: { xs: 1, sm: 0 },
            }}
          >
            TeamScout helps coaches and recruiters find and manage the best
            talent for their teams
          </Typography>

          <VideoContainer sx={{ mx: "auto", maxWidth: 900 }}>
            <Box
              component="img"
              src="/assets/images/teamscout-logo.png"
              alt="TeamScout Overview"
              sx={{ width: "100%", height: "auto", opacity: 0.8 }}
            />
            <PlayButton className="play-button">
              <PlayArrowIcon fontSize={isMobile ? "medium" : "large"} />
            </PlayButton>
          </VideoContainer>
        </Container>
      </VideoSection>

      <FeaturesSection>
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, mx: "auto" }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            align="center"
            gutterBottom
            sx={{ px: { xs: 1, sm: 0 } }}
          >
            Powerful Features for Recruiters
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "subtitle1"}
            color="text.secondary"
            align="center"
            sx={{
              mb: { xs: 4, sm: 6 },
              maxWidth: 700,
              mx: "auto",
              px: { xs: 1, sm: 0 },
            }}
          >
            Everything you need to streamline your recruiting process
          </Typography>

          <Grid
            container
            spacing={{ xs: 2, sm: 3, md: 4 }}
            justifyContent="center"
          >
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <FeatureIcon>{feature.icon}</FeatureIcon>
                    <Typography variant="h6" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </FeaturesSection>

      {mode !== "login" ? (
        <Container
          maxWidth="sm"
          sx={{
            py: { xs: 2, sm: 4 },
            px: { xs: 1, sm: 2 },
          }}
        >
          <AuthPaper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 4 },
              mx: { xs: 1, sm: 0 },
            }}
          >
            {renderContent()}
          </AuthPaper>
        </Container>
      ) : null}

      <Footer>
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, mx: "auto" }}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item xs={12} md={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: { xs: "center", md: "left" } }}
              >
                © All Rights Reserved 2025 AI Team Scout
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                direction="row"
                spacing={2}
                justifyContent={{ xs: "center", md: "flex-end" }}
                sx={{ flexWrap: "wrap" }}
              >
                <Link href="/privacy" color="inherit" variant="body2">
                  Privacy Policy
                </Link>
                <Link href="/terms" color="inherit" variant="body2">
                  Terms of Use
                </Link>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Footer>

      {/* Login Dialog */}
      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{
          "& .MuiDialog-paper": {
            margin: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "90vh" },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Sign In</Typography>
            <IconButton edge="end" onClick={() => setLoginDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <LoginForm
              onSubmit={handleLogin}
              loading={loading}
              error={error}
              onForgotPassword={() => {
                setMode("reset");
                setLoginDialogOpen(false);
              }}
              onRememberMeChange={(e) => setRememberMe(e.target.checked)}
              rememberMe={rememberMe}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </AuthContainer>
  );
};

export default EnhancedLogin;
